import type { QuotationResponse, AncillaryGroup } from '@cvo/domain';
import type { AllowedCartItem } from '~/types/cart';

/**
 * Builds the cart items array from quotation breakdown and resolved ancillaries.
 *
 * @param quotation - Reactive ref to the current quotation
 * @param ancillaries - Reactive ref to the resolved ancillaries from the quotation response
 */
export function useBookingCartItems(
  quotation: Ref<QuotationResponse | undefined>,
  ancillaries: ComputedRef<AncillaryGroup[] | null>,
) {
  const i18n = useI18n();
  const { lines: feeLines } = usePricingGroupLines(() => quotation.value?.pricing.summary.breakdown.fees, 'fees');
  const { lines: taxLines } = usePricingGroupLines(() => quotation.value?.pricing.summary.breakdown.taxes, 'taxes');

  const tripCartItems = computed<AllowedCartItem[]>(() => {
    if (quotation.value == null) return [];

    const { baseFare } = quotation.value.pricing.summary.breakdown;

    return [
      {
        id: 'fare',
        group: 'trip',
        name: i18n.t('ui.components.priceBreakdown.fare'),
        currency: baseFare.total.currency,
        type: 'fixed' as const,
        value: parseFloat(baseFare.total.amount),
        quantity: null,
      },
      ...[...feeLines.value, ...taxLines.value].map(line => ({
        id: line.id,
        group: 'trip',
        name: line.label,
        currency: line.price.currency,
        type: 'fixed' as const,
        value: parseFloat(line.price.amount),
        quantity: null,
      })),
    ];
  });

  const ancillaryCartItems = computed<AllowedCartItem[]>(() => {
    if (!Array.isArray(ancillaries.value)) return [];

    const items = ancillaries.value.flatMap(group => group.items.map(item => ({
      id: `ancillary-${item.type}`,
      group: 'ancillaries',
      name: i18n.t(`custom.ancillaries.${item.type}`),
      currency: item.price.currency,
      type: 'fixed' as const,
      value: parseFloat(item.price.amount) === 0
        ? i18n.t('ui.booking.cart.ancillaries.included')
        : parseFloat(item.price.amount),
      description: i18n.t('ui.booking.cart.ancillaries.allSegments'),
      quantity: item.quantity,
    })));

    // eslint-disable-next-line id-length
    return items.sort((a, b) => {
      const aIsIncluded = typeof a.value === 'string' ? 0 : 1;
      const bIsIncluded = typeof b.value === 'string' ? 0 : 1;
      return aIsIncluded - bIsIncluded;
    });
  });

  const cartItems = computed<AllowedCartItem[]>(() => [
    ...ancillaryCartItems.value,
    ...tripCartItems.value,
  ]);

  return { cartItems };
}
